import { Box, Link, Tooltip, Typography, Stack } from '@mui/material';
import { useState } from 'react';
import { translateErrorMessage, validatePassword } from '../../../../../utils/authHelpers';
import { AUTH_API } from '../../../../../api/amplify';
import { INCORRECT_PASSWORD } from '../../../../../constants/validationErrorMessages';
import { FormButton, FormInput } from '../../../../UI';
import PasswordTooltip from '../../components/PasswordTooltip';
import { PASSWORD_CHANGE_SUCCESS } from '../../../../../constants/authMessages';
import { useAppDispatch, useAppSelector, useForm } from '../../../../../hooks';
import { useNavigate } from 'react-router-dom';
import { setAuthForm, setCognitoUserAuthorized } from '../../../../../store/CognitoSlice';

import { AlertType } from '../../../../../types/common';
import { showAlert } from '../../../../../store';
import { PATHS } from '../../../../../constants/routes';
import { AuthForm } from '../../../../../types/auth';

type SetNewPasswordFormData = {
  code: string;
  password: string;
};

const INIT_USER: SetNewPasswordFormData = {
  code: '',
  password: '',
};

export const ChangePassword = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { input, error, handleChange, submit, handlePressEnter, setError, resetForm } = useForm(
    INIT_USER,
    handleSubmit
  );

  const [isLoading, setIsLoading] = useState(false);

  const {
    cognitoUser: { email },
  } = useAppSelector((state) => state.cognito);

  async function handleSubmit() {
    try {
      if (!validatePassword(input.password)) {
        setError((prev) => ({
          ...prev,
          password: INCORRECT_PASSWORD,
        }));
        return;
      }

      setIsLoading(true);

      const { code, password } = input;

      await AUTH_API.forgotPasswordSubmit(email, code, password);
      dispatch(showAlert({ type: AlertType.Success, message: PASSWORD_CHANGE_SUCCESS }));
      const response = await AUTH_API.signIn(email, password);

      if (response) {
        dispatch(setCognitoUserAuthorized());
        navigate(PATHS.dashboard);
      }

      resetForm();
    } catch (e: any) {
      const errorMessage = translateErrorMessage(e);
      dispatch(showAlert({ type: AlertType.Error, message: errorMessage }));
    }

    setIsLoading(false);
  }

  const switchToSigninForm = () => {
    dispatch(setAuthForm(AuthForm.Signin));
  };

  return (
    <Stack
      sx={{
        width: '100%',
        maxWidth: '400px',
        margin: '0 auto',
        gap: '60px',
      }}
    >
      <Stack onKeyDown={handlePressEnter}>
        <Typography
          variant="h1"
          mb={'30px'}
          align={'center'}
          sx={{
            typography: {
              mobile: 'h3',
              laptop: 'h1',
            },
          }}
        >
          Сменить пароль
        </Typography>

        <Typography variant="body1" mb={'30px'} align={'center'}>
          Введите код, отправленный на email
        </Typography>

        <Stack gap={'20px'} component={'form'} onSubmit={submit}>
          <Box>
            <FormInput
              onChange={handleChange}
              error={error?.code || ''}
              name={'code'}
              value={input.code || ''}
              placeholder={'Код'}
              label={'Код'}
              smallLabel
            />
            <Tooltip
              title={<PasswordTooltip password={input.password} />}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#fff',
                    padding: 0,
                    boxShadow:
                      '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
                  },
                },
              }}
              disableHoverListener
              placement={'right-end'}
            >
              <Stack sx={{}}>
                <FormInput
                  onChange={handleChange}
                  error={error?.password || ''}
                  name={'password'}
                  value={input.password || ''}
                  placeholder={'Новый пароль'}
                  label={'Пароль'}
                  type={'password'}
                  smallLabel
                />
              </Stack>
            </Tooltip>
          </Box>
          <Stack gap={'20px'}>
            <FormButton submit isLoading={isLoading}>
              Сменить пароль
            </FormButton>
            <Box component={'span'}>
              <Typography variant="subtitle1" component={'span'}>
                Вспомнили пароль?{' '}
              </Typography>

              <Link component={'button'} onClick={switchToSigninForm} underline={'none'} type={'button'}>
                Войти
              </Link>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
